import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  Fab,
  Grid,
  IconButton,
  Rating,
  Typography,
} from '@mui/material';

import { TabContext, TabPanel } from '@mui/lab';

import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { YouTubeEvent } from 'react-youtube';

import Can from './can';
import ContentPlayer from './contentplayer';
import { PayLock, RedLock, YellowLock } from './locks';
import ResultTabs from './result_tabs';
import ShareLinks from './ShareLinks';
import { useJiang } from '../utils/JiangContext';

export type ResultType = {
  '標題': string,
  '完整標題'?: string,
  '星等': string,
  '時間': number,
  '上傳YOUTUBE': string,
  '病名': string,
  '科別'?: string,
  'etiology 原因'?: string,
  'keyword 1'?: string,
  'keyword 2'?: string,
  'keyword 3'?: string,
  '病人階級'?: string,
  '主持人'?: string,
  '講者'?: string,
  '摘要'?: string,
  // '黃區連結'?: string,
  // '紅區連結'?: string,
  '分級GYR 版權及權限': 'G' | 'Y' | 'R' | 'PAY'
  'L0'?: string,
  'L1'?: string,
  'L4'?: string,
  '中文病名'?: string,
}

export interface IResultCardProps {
  item: ResultType;
  homeView?: boolean;
  expandMode?: boolean;
  setExpandMode?: Dispatch<SetStateAction<boolean>>;
  lastPlayed?: string | null;
  setLastPlayed?: Dispatch<SetStateAction<string | undefined>>;
  listIndex?: number;
}

const date_converter = (time: number) => {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Excel's day 1 is 1900-01-01
  const millisPerDay = 24 * 60 * 60 * 1000;
  const a = new Date(excelEpoch.getTime() + time * millisPerDay);
  return a.toISOString().split('T')[0].replaceAll('-', '/')
}

const level_splitter = (level: 'R' | 'Y' | 'G' | 'PAY') => {
  const roles = ['admin', 'memberR', 'memberY', 'memberG', 'guest'] as const;
  const levelOrder = ['R', 'Y', 'G'];
  const levelIndex = levelOrder.indexOf(level);

  // +2 because:
  // admin is always included
  // memberR starts at index 1
  const splitIndex = levelIndex === 2 ? levelIndex + 3 : levelIndex + 2;

  return [
    roles.slice(0, splitIndex),
    roles.slice(splitIndex),
  ];
};

export const ResultCard = (props: IResultCardProps) => {

  const { homeView, expandMode, setExpandMode, lastPlayed, setLastPlayed, listIndex } = props;
  const { bigger_than_xs } = useJiang();
  const resultCardRef = useRef<HTMLDivElement>(null);
  const [expandMore, setExpandMore] = useState<boolean>(false);

  let speaker = props.item['講者'] ?? '';
  let moderator = props.item['主持人'] ?? '';
  if (speaker === '' && moderator === '') {
    speaker = '黃清相醫師';
  }

  // Having mb: 1 for when chips wrap to next line, so all spacing is done via each chip's mb not their container
  const catChip = props.item['L0'] && <Chip label={`科別: ${props.item['L0']}`} sx={{ mr: 0.5, mb: 1 }} />;
  const nameChip = props.item['L1'] &&
    <Chip label={`病名: ${props.item['L1']} ${props.item['中文病名'] ?? ''}`} sx={{ mr: 0.5, mb: 1 }} />;

  const levelChip = props.item['病人階級'] && <Chip label={props.item['病人階級']} sx={{ mr: 0.5, mb: 1 }} />;
  const key1Chip = props.item['keyword 1'] && <Chip label={props.item['keyword 1']} sx={{ mr: 0.5, mb: 1 }} />;
  const key2Chip = props.item['keyword 2'] && <Chip label={props.item['keyword 2']} sx={{ mr: 0.5, mb: 1 }} />;
  const key3Chip = props.item['keyword 3'] && <Chip label={props.item['keyword 3']} sx={{ mr: 0.5, mb: 1 }} />;

  /* Tabs, Youtube variables or functions */
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);

  const scrollResultCard = async (how?: ScrollBehavior) => {
    if (listIndex === 0) {
      window.scrollTo({ top: 0 });
    } else {
      await new Promise(resolve => setTimeout(resolve, 300));
      resultCardRef?.current?.scrollIntoView({ behavior: how ? how : 'auto', block: 'center' });
    }
  }

  const handleVideoStateChange = async (event: YouTubeEvent) => {
    const player = event.target;
    const state = player.getPlayerState(); // number

    // 1 === PLAYING according to YouTube IFrame API
    if (state === 1) {
      if (!hasPlayed) {
        setHasPlayed(true);
        if (bigger_than_xs) {
          setExpandMode && setExpandMode(true);
          !homeView && scrollResultCard('smooth');
        }
      }
      setLastPlayed && setLastPlayed(props.item['上傳YOUTUBE']);
    }
  }

  useEffect(() => {
    if (lastPlayed === props.item['上傳YOUTUBE']) {
      scrollResultCard();
    }
  }, [expandMode]);

  // Temp, not-using-bundle way
  const colorMap = ['G', 'Y', 'R', 'PAY'];
  const level = props.item['分級GYR 版權及權限'] || 'G'; // || to catch '', undefined, and null, default to G
  const [tabValue, setTabValue] = useState<number>(colorMap.indexOf(level));
  let allow_array = level_splitter(level);
  let canvideo = allow_array[0];
  let canlock = allow_array[1];
  let lock = <></>;
  switch(level) {
    case 'Y':
      lock = <YellowLock />;
      break;
    case 'R':
      lock = <RedLock />;
      break;
    case 'PAY':
      lock = <PayLock />;
      break;
  };
  // console.log('canvideo', canvideo);
  // console.log('canlock', canlock);

  // Maybe useful for future when bundling
  // const hasYellow = props.item.黃區連結 !== undefined;
  // const hasRed = props.item.紅區連結 !== undefined;
  // let show: boolean[] = [false, true, false];
  // console.log('hasYellow', hasYellow);
  // console.log('hasRed', hasRed);
  // console.log(props.item);

  // useEffect(() => {
  // console.log('props item', props.item);
  // date_converter(props.item['時間']);
  // });

  // Title clamping sx
  const clamp2Lines = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  return (
    <Grid container spacing={4} sx={{ mb: 4 }} ref={resultCardRef}>
      <Grid size={{ xs: 12 }}>
        {/* <ResultTabs value={tabValue} setValue={setTabValue} show={show} /> */}
        <ResultTabs value={tabValue} setValue={setTabValue} />
        <Card sx={(theme) => ({
          boxShadow: theme.shadows[1].replace(
            /rgba\(0,0,0,[^)]+\)/g,
            level === 'R' ? theme.palette.error.light.replace('1)', '0.3)')
              : level === 'Y' ? theme.palette.secondary.main.replace('1)', '0.3)')
              : level === 'PAY' ? theme.palette.secondary.light.replace('1)', '0.3)')
                : theme.palette.primary.light.replace('1)', '0.1)')
          ),
        })}>
          <Grid container
            sx={{
              flexDirection: homeView ? {
                xs: 'column-reverse', // reverse on small screens
              } : {
                xs: 'column-reverse', // reverse on small screens
                sm: 'row',             // normal on >=600px
              }
            }}
          >
            <Grid size={{ xs: 12, sm: homeView ? 12 : expandMode ? 3 : 8 }} sx={{ transition: 'all 0.3s ease' }}>
              {/* I still have no idea why 94%, it wasn't like this when I first tried */}
              {/* Also I don't know why I have to use the style to set paddingBottom */}
              {/* Having flex here to push in ID to the bottom in expandMode */}
              <CardContent
                sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '94%' }}
                style={{ paddingBottom: `${expandMode ? 16 : 8}px` }}
              >
                <Box>
                  <Typography variant={homeView ? 'body1' : 'h5'} sx={{
                    height: (homeView && !expandMore) ? 64 : 'auto',
                    ...(!expandMode && clamp2Lines), // only show if it is not expanded
                    ...(homeView && { fontSize: 20 }), // only show if it is in homeView
                  }}>{props.item['標題']}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                    {props.item['時間'] &&
                      <Typography variant='subtitle1' sx={{ mr: 0.5 }}>
                        {props.item['時間'] && date_converter(props.item['時間'])}
                      </Typography>
                    }
                    {props.item['星等'] &&
                      <Rating
                        name='read-only'
                        value={props.item['星等'].length}
                        max={props.item['星等'].length}
                        readOnly
                      />
                    }
                    {props.item['L4'] &&
                      <Typography variant='body2' color='gray' sx={{ ml: 1, mr: 0.5 }}>{props.item['L4']}</Typography>
                    }
                  </Box>
                  {!expandMode && !props.item['L4'] && !homeView
                    ? <Typography variant='body2' color='gray' noWrap>溝通碼: {props.item['完整標題']}</Typography>
                    : <></>
                  }
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='subtitle2'>
                      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        {
                          speaker !== '' && <Typography variant='subtitle2'>{speaker}</Typography>
                        }
                        {
                          moderator !== '' &&
                          <>
                            <Typography variant='body1' sx={{ marginLeft: 0.7, marginRight: 0.7 }}>·</Typography>
                            <Typography variant='subtitle2'>{moderator}</Typography>
                          </>
                        }
                      </Box>
                    </Typography>
                    {!expandMode && <IconButton
                      onClick={() => setExpandMore(!expandMore)}
                      size='small'
                    >
                      <ExpandMore />
                    </IconButton>}
                  </Box>
                  <Collapse in={expandMode}>
                    <Box sx={{ mt: 1 }}>
                      {catChip}
                      {nameChip}
                    </Box>
                    <Box>
                      {levelChip}
                      {key1Chip}
                      {key2Chip}
                      {key3Chip}
                    </Box>
                    <Box sx={{ mt: 1, mb: 2, maxHeight: '250px', overflow: 'auto', textOverflow: 'ellipsis' }}>
                      {props.item['摘要'] ??
                        <Typography variant='body1'>{props.item['摘要']}</Typography>
                      }
                    </Box>
                  </Collapse>
                </Box>
                {expandMode &&
                  <Typography variant='body2' color='gray'>溝通碼: {props.item['完整標題']}</Typography>
                }
              </CardContent>
            </Grid>
            <Grid
              size={{ xs: 12, sm: homeView ? 12 : expandMode ? 9 : 4 }}
              sx={{
                aspectRatio: `16 / ${(expandMode || !bigger_than_xs || homeView) ? 8 : 4}`,
                transition: 'all 0.3s ease'
              }}
            >
              {/* <TabContext value={tabValue}> */}
              <TabContext value={0}>
                <TabPanel value={0} keepMounted sx={{ padding: 0, width: '100%', height: '100%' }}>
                  <Can roles={canvideo}>
                    <ContentPlayer
                      link={props.item['上傳YOUTUBE']}
                      onStateChange={handleVideoStateChange}
                      level={level}
                    />
                  </Can>
                  <Can roles={canlock}>
                    {lock}
                  </Can>
                </TabPanel>
                {/* <TabPanel value={1} keepMounted sx={{ padding: 0, width: '100%', height: '100%' }}>
                  <Can roles={['admin', 'memberR', 'memberY']}>
                    <ContentPlayer
                      link={props.item['黃區連結'] ?? ''}
                      onStateChange={handleVideoStateChange}
                    />
                  </Can>
                  <Can roles={['guest', 'memberG']}>
                    <YellowLock />
                  </Can>
                </TabPanel> */}
                {/* <TabPanel value={2} keepMounted sx={{ padding: 0, width: '100%', height: '100%' }}>
                  <RedLock />
                </TabPanel> */}
              </TabContext>
            </Grid>
          </Grid>
          <Collapse in={expandMore}>
            <Divider />
            <CardContent>
              <Box sx={{ mt: 1 }}>
                {catChip}
                {nameChip}
                {levelChip}
                {key1Chip}
                {key2Chip}
                {key3Chip}
              </Box>
              {props.item['摘要'] &&
                <Box sx={{ mt: 1, mb: 1, maxHeight: '250px', overflow: 'auto' }}>
                  <Typography variant='body1'>{props.item['摘要']}</Typography>
                </Box>
              }
              <Typography variant='body2' color='gray'>溝通碼: {props.item['完整標題']}</Typography>
            </CardContent>
          </Collapse>
        </Card>
      </Grid>
    </Grid>
  )
};

const Results = ({ results }: { results: ResultType[] }) => {

  const [expandMode, setExpandMode] = useState<boolean>(false);
  const [lastPlayed, setLastPlayed] = useState<string | undefined>(undefined);
  // if no video link in excel, will show as null, so here use undefined to separate

  const { bigger_than_xs } = useJiang();

  // useEffect(() => {
  // console.log('results length from results', results.length);
  // console.log('results length from results', results);
  // }, [results]);

  return (
    <>
      <Container
        maxWidth={expandMode ? 'xl' : 'md'}
        sx={{ mt: 2, mb: 4, transition: 'all 0.3s ease', flexGrow: 1 }}
      >
        {
          results.length > 0 ? (
            <>
              <Grid container sx={{ mt: 1, justifyContent: 'center' }}>
                <ShareLinks results={results} />
              </Grid>
              {
                results.map((r: ResultType, index: number) => (
                  <ResultCard
                    key={`${r.上傳YOUTUBE}${r.完整標題}`}
                    item={r}
                    expandMode={expandMode}
                    setExpandMode={setExpandMode}
                    lastPlayed={lastPlayed}
                    setLastPlayed={setLastPlayed}
                    listIndex={index}
                  />
                ))
              }
            </>
          ) : (
            <Typography>No results found</Typography>
          )
        }
      </Container>
      {bigger_than_xs &&
        <Fab
          color='primary'
          sx={{
            position: 'fixed',  // fixed position relative to viewport
            bottom: 32,         // 16px from bottom
            right: 32,          // 16px from right
            zIndex: (theme) => theme.zIndex.tooltip + 1, // ensure it floats above other content (optional)
          }}
          onClick={() => setExpandMode(!expandMode)}
        >
          {expandMode ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
        </Fab>
      }
    </>
  )
}

export default Results;